/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {
  forwardRef, useEffect, useMemo, useRef, useState,
} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';

import DisplayPrice from '@salesforce/retail-react-app/app/components/display-price';
import Link from '@salesforce/retail-react-app/app/components/link';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';
import {
  Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Button, Fade,
  Flex, Heading, Skeleton, Text, useMultiStyleConfig, useTheme, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration';
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants';
import {useCurrency, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks';
import {useAddToCartModalContext} from '@salesforce/retail-react-app/app/hooks/use-add-to-cart-modal';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils';

import {Markup} from '@amblique/base/app/components/shared/markup';
import {MinusIcon, PlusIcon, UnionIcon} from '@amblique/king-living/app/components/icons';
import ImageGallery, {Skeleton as ImageGallerySkeleton} from '@amblique/king-living/app/components/image-gallery';
import {VariationAttributes} from '@amblique/king-living/app/components/product-view/variation-attributes';

/**
 * ProductDescription Component
 * Renders product description that can be either HTML string or Contentful rich text JSON
 */
const ProductDescription = ({description}) => {
  if (!description) {
    return null;
  }

  return (
    <Box
      color="gray.700"
      fontSize="md"
      lineHeight="1.6"
      mt={2}
    >
      <Markup source={description} />
    </Box>
  );
};

ProductDescription.propTypes = {
  description: PropTypes.oneOfType([
    PropTypes.string
  ]),
};

const ProductViewHeader = ({
  name,
  currency,
  priceData,
  category,
  product,
  isProductPartOfBundle,
}) => {
  return (
    <VStack align="flex-start" marginBottom={[4, 4, 4, 0, 0]} mr={4} spacing={2}>
      {/* Title - Subcategory */}
      {product?.c_subCategory && (
        <Skeleton isLoaded={product?.c_subCategory}>
          <Heading fontSize="2xl">{product.c_subCategory}</Heading>
        </Skeleton>
      )}

      {product?.longDescription && (
        <Skeleton isLoaded={product?.longDescription}>
          <ProductDescription description={product.longDescription} />
        </Skeleton>
      )}

      {/* {!isProductPartOfBundle && (
        <>
          <Skeleton isLoaded={priceData?.currentPrice}>
            {priceData?.currentPrice && (
              <DisplayPrice currency={currency} priceData={priceData} />
            )}
          </Skeleton>

          <Skeleton isLoaded={product}>
            {product?.productPromotions && <PromoCallout product={product} />}
          </Skeleton>
        </>
      )} */}
    </VStack>
  );
};

ProductViewHeader.propTypes = {
  name: PropTypes.string,
  currency: PropTypes.string,
  category: PropTypes.array,
  priceData: PropTypes.object,
  product: PropTypes.object,
  isProductPartOfBundle: PropTypes.bool,
};

const ButtonWithRegistration = withRegistration(Button);

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
  (
    {
      product,
      category,
      showFullLink = false,
      imageSize = 'md',
      isWishlistLoading = false,
      addToCart,
      updateCart,
      addToWishlist,
      updateWishlist,
      isProductLoading,
      isProductPartOfSet = false,
      isProductPartOfBundle = false,
      childOfBundleQuantity = 0,
      childProductOrderability,
      setChildProductOrderability,
      isBasketLoading = false,
      onVariantSelected = () => { },
      validateOrderability = (variant, quantity, stockLevel) => !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
      showImageGallery = true,
      setSelectedBundleQuantity = () => { },
      selectedBundleParentQuantity = 1,
    },
    ref
  ) => {
    const {currency: activeCurrency} = useCurrency();
    const showToast = useToast();
    const intl = useIntl();
    const location = useLocation();
    const {
      isOpen: isAddToCartModalOpen,
      onOpen: onAddToCartModalOpen,
      onClose: onAddToCartModalClose,
    } = useAddToCartModalContext();
    const theme = useTheme();
    const styles = useMultiStyleConfig('ProductDetail', {});
    const [showOptionsMessage, toggleShowOptionsMessage] = useState(false);
    const [activeTab, setActiveTab] = useState('build');
    const {
      showLoading,
      showInventoryMessage,
      inventoryMessage,
      quantity,
      setQuantity,
      variant,
      variationParams,
      variationAttributes,
      stockLevel,
      isOutOfStock,
      unfulfillable,
      isSimpleProduct,
      isComplexProduct,
    } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle);
    const priceData = useMemo(() => {
      return getPriceData(product, {quantity});
    }, [product, quantity]);
    const canAddToWishlist = !isProductLoading;
    const isProductASet = product?.type.set;
    const isProductABundle = product?.type.bundle;
    const errorContainerRef = useRef(null);

    const {disableButton, customInventoryMessage} = useMemo(() => {
      let shouldDisableButton = showInventoryMessage;
      let currentInventoryMsg = '';

      if (
        !shouldDisableButton &&
        (isProductASet || isProductABundle) &&
        childProductOrderability
      ) {
        // if any of the children are not orderable, it will disable the add to cart button
        const unavailableChildProductKey = Object.keys(childProductOrderability).find(
          (key) => {
            return childProductOrderability[key].showInventoryMessage;
          }
        );

        shouldDisableButton = !!unavailableChildProductKey;
        if (unavailableChildProductKey) {
          const unavailableChildProduct =
            childProductOrderability[unavailableChildProductKey];

          if (unavailableChildProduct.unfulfillable) {
            currentInventoryMsg = intl.formatMessage(
              {
                defaultMessage: 'Only {stockLevel} left for {productName}!',
                id: 'use_product.message.inventory_remaining_for_product',
              },
              {
                stockLevel: unavailableChildProduct.stockLevel,
                productName: unavailableChildProduct.productName,
              }
            );
          }
          if (unavailableChildProduct.isOutOfStock) {
            currentInventoryMsg = intl.formatMessage(
              {
                defaultMessage: 'Out of stock for {productName}',
                id: 'use_product.message.out_of_stock_for_product',
              },
              {productName: unavailableChildProduct.productName}
            );
          }
        }
      }

      return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg};
    }, [showInventoryMessage, childProductOrderability]);

    // Handle tab click for Build & Customize / Specifications tabs
    const handleTabClick = (tab) => {
      setActiveTab(tab);
      if (tab === 'specifications') {
        const specificationsSection = document.getElementById('specifications');

        if (specificationsSection) {
          specificationsSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      } else if (tab === 'build') {
        // Scroll to top of product view
        if (ref?.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    };

    const validateAndShowError = (opts = {}) => {
      const {scrollErrorIntoView = true} = opts;
      // Validate that all attributes are selected before proceeding.
      const hasValidSelection = validateOrderability(variant, quantity, stockLevel);
      const showError = !isProductASet && !isProductABundle && !hasValidSelection;
      const scrollToError = showError && scrollErrorIntoView;

      toggleShowOptionsMessage(showError);

      if (scrollToError) {
        errorContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      return hasValidSelection;
    };

    const renderActionButtons = () => {
      const buttons = [];
      const buttonText = {
        update: intl.formatMessage({
          defaultMessage: 'Update',
          id: 'product_view.button.update',
        }),
        addToCart: intl.formatMessage({
          defaultMessage: 'Add to Cart',
          id: 'product_view.button.add_to_cart',
        }),
        addSetToCart: intl.formatMessage({
          defaultMessage: 'Add Set to Cart',
          id: 'product_view.button.add_set_to_cart',
        }),
        addBundleToCart: intl.formatMessage({
          defaultMessage: 'Add Bundle to Cart',
          id: 'product_view.button.add_bundle_to_cart',
        }),
        addToWishlist: intl.formatMessage({
          defaultMessage: 'Add to Wishlist',
          id: 'product_view.button.add_to_wishlist',
        }),
        addSetToWishlist: intl.formatMessage({
          defaultMessage: 'Add Set to Wishlist',
          id: 'product_view.button.add_set_to_wishlist',
        }),
        addBundleToWishlist: intl.formatMessage({
          defaultMessage: 'Add Bundle to Wishlist',
          id: 'product_view.button.add_bundle_to_wishlist',
        }),
      };

      const showError = () => {
        showToast({
          title: intl.formatMessage(API_ERROR_MESSAGE),
          status: 'error',
        });
      };

      const handleCartItem = async () => {
        const hasValidSelection = validateAndShowError();

        if (!hasValidSelection) {
          return null;
        }

        if (!addToCart && !updateCart) return null;
        if (updateCart) {
          await updateCart(variant || product, quantity);

          return;
        }
        try {
          const itemsAdded = await addToCart(variant, quantity);

          // Open modal only when `addToCart` returns some data
          // It's possible that the item has been added to cart, but we don't want to open the modal.
          // See wishlist_primary_action for example.
          if (itemsAdded) {
            onAddToCartModalOpen({
              product,
              itemsAdded,
              selectedQuantity: quantity,
            });
          }
        } catch (e) {
          showError();
        }
      };

      const handleWishlistItem = async () => {
        if (!updateWishlist && !addToWishlist) return null;
        if (updateWishlist) {
          updateWishlist(product, variant, quantity);

          return;
        }
        addToWishlist(product, variant, quantity);
      };

      // child product of bundles do not have add to cart button
      if ((addToCart || updateCart) && !isProductPartOfBundle) {
        // Format button text with price for regular products
        const currency = product?.currency || activeCurrency;
        const formattedPrice = priceData?.currentPrice ?
          intl.formatNumber(priceData.currentPrice, {
            currency,
            style: 'currency',
          }) :
          '';

        const buttonTextWithPrice = updateCart ?
          buttonText.update :
          isProductASet ?
            buttonText.addSetToCart :
            isProductABundle ?
              buttonText.addBundleToCart :
              `${buttonText.addToCart}${formattedPrice ? ` - ${formattedPrice}` : ''}`;

        buttons.push(
          <Button
            key="cart-button"
            isDisabled={disableButton}
            isLoading={isBasketLoading}
            marginBottom={4}
            variant="filled-light"
            width="100%"
            onClick={handleCartItem}
          >
            {buttonTextWithPrice}
          </Button>
        );
      }

      // child product of bundles do not have add to wishlist button
      if ((addToWishlist || updateWishlist) && !isProductPartOfBundle) {
        buttons.push(
          <ButtonWithRegistration
            key="wishlist-button"
            disabled={isWishlistLoading || !canAddToWishlist}
            isLoading={isWishlistLoading}
            marginBottom={4}
            variant="outlined-light"
            width="100%"
            onClick={handleWishlistItem}
          >
            <UnionIcon boxSize={4} />
            {updateWishlist ?
              buttonText.update :
              isProductASet ?
                buttonText.addSetToWishlist :
                isProductABundle ?
                  buttonText.addBundleToWishlist :
                  buttonText.addToWishlist}
          </ButtonWithRegistration>
        );
      }

      return buttons;
    };

    // Bind the reference with our `scope` that includes the internal validate function for this component.
    // Other values can be added to this scope as required.
    if (typeof ref === 'function') {
      ref = ref.bind({validateOrderability: validateAndShowError});
    }

    // Set the quantity of bundle child in a product bundle to ensure availability messages appear
    if (
      isProductPartOfBundle &&
      quantity != selectedBundleParentQuantity * childOfBundleQuantity
    ) {
      setQuantity(selectedBundleParentQuantity * childOfBundleQuantity);
    }

    useEffect(() => {
      if (isAddToCartModalOpen) {
        onAddToCartModalClose();
      }
    }, [location.pathname]);

    useEffect(() => {
      if (
        !isProductASet &&
        !isProductABundle &&
        validateOrderability(variant, quantity, stockLevel)
      ) {
        toggleShowOptionsMessage(false);
      }
    }, [variationParams]);

    useEffect(() => {
      if (variant) {
        onVariantSelected(product, variant, quantity);
      }
    }, [variant?.productId, quantity]);

    useEffect(() => {
      if (isProductPartOfBundle || isProductPartOfSet) {
        const key = product.itemId ?? product.id;

        // when showInventoryMessage is true, it means child product is not orderable
        setChildProductOrderability((previousState) => ({
          ...previousState,
          [key]: {
            showInventoryMessage,
            isOutOfStock,
            unfulfillable,
            stockLevel,
            productName: product?.name,
          },
        }));
      }
    }, [showInventoryMessage, inventoryMessage]);

    return (
      <Flex ref={ref} data-testid="product-view" direction="column">
        <Flex direction={['column', 'column', 'column', 'row']} gap={{base: '0', lg: '80px'}}>
          {showImageGallery && (
            <Box flex={1} flexBasis={{base: '100%', lg: '60%'}} width={{base: '100%', lg: 'calc(60% - 80px * 0.6)'}}>
              {product ? (
                <>
                  <ImageGallery
                    isSimpleProduct={isSimpleProduct}
                    lazy={isProductPartOfSet || isProductPartOfBundle}
                    product={product}
                    selectedVariationAttributes={variationParams}
                    size={imageSize}
                  />

                  {showFullLink && product && (
                    <HideOnMobile>
                      <Link
                        color="blue.600"
                        to={`/product/${product.master.masterId}`}
                      >
                        <FormattedMessage
                          defaultMessage="See full details"
                          id="product_view.link.full_details"
                        />
                      </Link>
                    </HideOnMobile>
                  )}
                </>
              ) : (
                <ImageGallerySkeleton />
              )}
            </Box>
          )}

          {/* Variations & Quantity Selector & CTA buttons */}
          <VStack align="stretch" flex={1} flexBasis={{base: '100%', lg: '40%'}} spacing={8}>
            <ProductViewHeader
              category={category}
              currency={product?.currency || activeCurrency}
              isProductPartOfBundle={isProductPartOfBundle}
              name={product?.name}
              priceData={priceData}
              product={product}
            />
            <VStack align="stretch" spacing={4}>
              {isProductPartOfBundle && (
                <Box>
                  <Text aria-label="price" fontSize="md" fontWeight="medium">
                    <label>
                      {intl.formatMessage({
                        defaultMessage: 'Quantity',
                        id: 'product_view.label.quantity',
                      })}
                      : {childOfBundleQuantity}
                    </label>
                  </Text>
                </Box>
              )}

              {/* Information Tabs - Build & Customise and Specifications - For Complex Products Only */}
              {isComplexProduct && (
                <Box marginTop={6}>
                  <Box {...styles.productTabsInformationContainer}>
                    <Box
                      {...styles.productTabsInformationTab}
                      {...styles[activeTab === 'build' ? 'productTabsInformationTabActive' : 'productTabsInformationTabInactive']}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleTabClick('build')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleTabClick('build');
                        }
                      }}
                    >
                      <Text
                        {...styles.productTabsInformationTabText}
                        color="brandColors.greyscale"
                        opacity={activeTab === 'build' ? 1 : 0.5}
                        transition="opacity 0.2s ease"
                      >
                        {intl.formatMessage({
                          defaultMessage: 'Build & Customize',
                          id: 'product_detail.tab.title.build_and_customise',
                        })}
                      </Text>
                    </Box>
                    <Box
                      {...styles.productTabsInformationTab}
                      {...styles[activeTab === 'specifications' ? 'productTabsInformationTabActive' : 'productTabsInformationTabInactive']}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleTabClick('specifications')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleTabClick('specifications');
                        }
                      }}
                    >
                      <Text
                        {...styles.productTabsInformationTabText}
                        color="brandColors.greyscale"
                        opacity={activeTab === 'specifications' ? 1 : 0.5}
                        transition="opacity 0.2s ease"
                      >
                        {intl.formatMessage({
                          defaultMessage: 'Specifications',
                          id: 'product_detail.tab.title.specifications',
                        })}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              )}

              <VariationAttributes
                isComplexProduct={isComplexProduct}
                isSimpleProduct={isSimpleProduct}
                product={product}
                showLoading={showLoading}
                variationAttributes={variationAttributes}
              />

              {/* Promotion Callout Accordion - For Complex Products Only */}
              {isComplexProduct && product?.productPromotions && product.productPromotions.length > 0 && (
                <Box mt={6}>
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      {({isExpanded}) => (
                        <>
                          <h3>
                            <AccordionButton
                              _hover={{bg: 'gray.50'}}
                              px={6}
                              py={4}
                            >
                              <Box flex="1" fontWeight="semibold" textAlign="left">
                                {intl.formatMessage({
                                  defaultMessage: 'Free accessory package offer',
                                  id: 'product_view.promotion.title.free_accessory_package',
                                })}
                              </Box>
                              {isExpanded ? (
                                <MinusIcon boxSize={5} />
                              ) : (
                                <PlusIcon boxSize={5} />
                              )}
                            </AccordionButton>
                          </h3>
                          <AccordionPanel pb={4} px={6}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: product.productPromotions[0].calloutMsg,
                              }}
                            />
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              <Box ref={errorContainerRef}>
                {!showLoading && showOptionsMessage && (
                  <Fade in={true}>
                    <Text color="orange.600" fontWeight={600} marginBottom={8}>
                      {intl.formatMessage({
                        defaultMessage:
                          'Please select all your options above',
                      })}
                    </Text>
                  </Fade>
                )}
              </Box>
              <HideOnDesktop>
                {showFullLink && product && (
                  <Link
                    color="blue.600"
                    to={`/product/${product.master.masterId}`}
                  >
                    <FormattedMessage
                      defaultMessage="See full details"
                      id="product_view.link.full_details"
                    />
                  </Link>
                )}
              </HideOnDesktop>
              {isProductASet && <p>{product?.shortDescription}</p>}
            </VStack>

            {/* Product Action Section: In Stock, Total Price, Add to Cart */}
            {!isProductPartOfBundle && !isProductPartOfSet && (
              <VStack align="stretch" spacing={4}>
                {/* In Stock Indicator */}
                {!isOutOfStock && stockLevel > 0 && (
                  <Flex align="center" gap={2}>
                    <Box height="16px" position="relative" width="16px">
                      {/* Breathing animation circle */}
                      <Box
                        backgroundColor="green.500"
                        borderRadius="50%"
                        height="8px"
                        left="50%"
                        opacity={0.3}
                        position="absolute"
                        top="50%"
                        transform="translate(-50%, -50%)"
                        width="8px"
                        zIndex={1}
                        sx={{
                          '@keyframes breathing': {
                            '0%': {
                              opacity: 0.3,
                              transform: 'translate(-50%, -50%) scale(1)',
                            },
                            '50%': {
                              opacity: 0.1,
                              transform: 'translate(-50%, -50%) scale(1.8)',
                            },
                            '100%': {
                              opacity: 0.3,
                              transform: 'translate(-50%, -50%) scale(1)',
                            },
                          },
                          'animation': 'breathing 2s ease-in-out infinite',
                        }}
                      />
                      {/* Solid green dot */}
                      <Box
                        backgroundColor="green.500"
                        borderRadius="50%"
                        height="8px"
                        left="50%"
                        position="absolute"
                        top="50%"
                        transform="translate(-50%, -50%)"
                        width="8px"
                        zIndex={2}
                      />
                    </Box>
                    <Text color="gray.800" fontSize="md" fontWeight="normal">
                      <FormattedMessage
                        defaultMessage="In stock"
                        id="product_view.stock.in_stock"
                      />
                    </Text>
                  </Flex>
                )}

                {/* Total Price Section */}
                <Box>
                  <Text color="gray.600" fontSize="sm" fontWeight="normal" marginBottom={2}>
                    <FormattedMessage
                      defaultMessage="Total price"
                      id="product_view.price.heading"
                    />
                  </Text>
                  {priceData?.currentPrice && (
                    <Box color="gray.800" fontSize="xl" fontWeight="bold">
                      <DisplayPrice currency={product?.currency || activeCurrency} priceData={priceData} />
                    </Box>
                  )}
                </Box>

              </VStack>
            )}

            <Box>
              {!showLoading && showInventoryMessage && !customInventoryMessage && (
                <Fade in={true}>
                  <Text color="orange.600" fontWeight={600} marginBottom={8}>
                    {inventoryMessage}
                  </Text>
                </Fade>
              )}
              {!showLoading && customInventoryMessage && (
                <Fade in={true}>
                  <Text color="orange.600" fontWeight={600} marginBottom={8}>
                    {customInventoryMessage}
                  </Text>
                </Fade>
              )}
              <Box
                display={
                  isProductPartOfSet ? 'block' : ['none', 'none', 'none', 'block']
                }
              >
                {renderActionButtons()}
              </Box>
            </Box>
          </VStack>
        </Flex>

        {/* Sticky call-to-action buttons for mobile and certain product types */}
        <Box
          bg="white"
          bottom={0}
          boxShadow={theme.shadows.top}
          left={0}
          p={[4, 4, 6]}
          position="fixed"
          width="100%"
          zIndex={2}
          display={
            isProductPartOfSet || isProductPartOfBundle ?
              'none' :
              ['block', 'block', 'block', 'none']
          }
        >
          {renderActionButtons()}
        </Box>
      </Flex>
    );
  }
);

ProductView.displayName = 'ProductView';

ProductView.propTypes = {
  product: PropTypes.object,
  isProductPartOfSet: PropTypes.bool,
  isProductPartOfBundle: PropTypes.bool,
  childOfBundleQuantity: PropTypes.number,
  category: PropTypes.array,
  isProductLoading: PropTypes.bool,
  isBasketLoading: PropTypes.bool,
  isWishlistLoading: PropTypes.bool,
  addToCart: PropTypes.func,
  addToWishlist: PropTypes.func,
  updateCart: PropTypes.func,
  updateWishlist: PropTypes.func,
  showFullLink: PropTypes.bool,
  imageSize: PropTypes.oneOf(['sm', 'md']),
  childProductOrderability: PropTypes.object,
  setChildProductOrderability: PropTypes.func,
  onVariantSelected: PropTypes.func,
  validateOrderability: PropTypes.func,
  showImageGallery: PropTypes.bool,
  setSelectedBundleQuantity: PropTypes.func,
  selectedBundleParentQuantity: PropTypes.number,
};

export default ProductView;
